import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Confirmation } from "./VoiceClonerModals/Confirmation";
import { CustomVoiceEditor } from "./VoiceClonerModals/CustomVoiceEditor";
import { PresetVoiceSelector } from "./VoiceClonerModals/PresetVoiceSelector";
import { TypeSelector } from "./VoiceClonerModals/TypeSelector";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
};

export type Step = {
  id: number;
  name: string;
  status: "complete" | "current" | "upcoming";
};

export type VoiceType = "Preset" | "Custom";

export type VoiceClonerData = {
  voiceType: VoiceType;
  selectedLanguage?: string;
  presetVoiceId?: string;
  presetVoiceName?: string;
  customVoiceId?: string;
  customVoiceName?: string;
  audioSamples?: File[];
};

export const VoiceClonerModal: FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();

  const [voiceClonerData, setVoiceClonerData] = useState<VoiceClonerData>({
    voiceType: "Preset",
  });

  const getStepsForVoiceType = (voiceType: VoiceType): Step[] => {
    const commonSteps: Step[] = [
      { id: 1, name: t("voiceCloner.selectType"), status: "current" },
    ];

    if (voiceType === "Preset") {
      return [
        ...commonSteps,
        {
          id: 2,
          name: t("voiceCloner.selectPreset"),
          status: "upcoming",
        },
        {
          id: 3,
          name: t("voiceCloner.confirmation"),
          status: "upcoming",
        },
      ];
    } else {
      return [
        ...commonSteps,
        {
          id: 2,
          name: t("voiceCloner.creation"),
          status: "upcoming",
        },
        {
          id: 3,
          name: t("voiceCloner.confirmation"),
          status: "upcoming",
        },
      ];
    }
  };

  const [steps, setSteps] = useState<Step[]>(getStepsForVoiceType("Preset"));

  const currentStep = steps.find((step) => step.status === "current");

  const updateDataAndNextStep = (newData: Partial<VoiceClonerData>) => {
    // Met à jour les données du voice cloner avec les nouvelles données
    setVoiceClonerData((prev) => ({ ...prev, ...newData }));

    // Si on change le type de voix à l'étape 1, on reconfigure les étapes
    if (currentStep?.id === 1 && newData.voiceType) {
      const newSteps = getStepsForVoiceType(newData.voiceType);
      setSteps(
        newSteps.map((step, index) => ({
          ...step,
          status:
            index === 0 ? "complete" : index === 1 ? "current" : "upcoming",
        }))
      );
    } else {
      // Sinon, on passe à l'étape suivante (avec les bonnes étapes)
      nextStep();
    }
  };

  const nextStep = () => {
    setSteps((prevSteps) => {
      const currentStepIndex = prevSteps.findIndex(
        (step) => step.status === "current"
      );
      if (currentStepIndex === prevSteps.length - 1) return prevSteps;

      return prevSteps.map((step, index) => {
        if (index === currentStepIndex) {
          return { ...step, status: "complete" };
        } else if (index === currentStepIndex + 1) {
          return { ...step, status: "current" };
        }
        return step;
      });
    });
  };

  const resetModal = () => {
    setVoiceClonerData({ voiceType: "Preset" });
    setSteps(getStepsForVoiceType("Preset"));
  };

  const handleClose = () => {
    setOpen(false);
    resetModal();
  };

  const renderCurrentStep = () => {
    if (!currentStep) return null;

    switch (currentStep.id) {
      case 1:
        return (
          <TypeSelector
            open={open}
            setOpen={handleClose}
            onNext={updateDataAndNextStep}
          />
        );

      case 2:
        if (voiceClonerData.voiceType === "Preset") {
          return (
            <PresetVoiceSelector
              open={open}
              setOpen={handleClose}
              onNext={updateDataAndNextStep}
            />
          );
        } else {
          return (
            <CustomVoiceEditor
              open={open}
              setOpen={handleClose}
              onNext={updateDataAndNextStep}
              steps={steps}
            />
          );
        }

      case 3:
        return (
          <Confirmation
            open={open}
            setOpen={handleClose}
            voiceClonerData={voiceClonerData}
            resetModal={resetModal}
          />
        );
      default:
        return null;
    }
  };

  return renderCurrentStep();
};
